import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { inject } from '@angular/core';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { LoginState } from '@pxl/models/common';
import { ChatbotService, ChatMessage, LoginService, UserService } from '@pxl/models/contracts';
import { LanguageService, RESOURCES } from '@pxl/shared/translation';
import type { ResizeGeometry } from '@pxl/ui';
import type { Observable } from 'rxjs';
import { EMPTY, filter, of, pipe, switchMap } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ChatbotEntry, ChatbotFeedback } from './chatbot.models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type ChatbotState = {
    entries: ChatbotEntry[];
    isLoading: boolean;
    hasError: boolean;
    isOpen: boolean;
    isInitialized: boolean;
    fullScreen: boolean;
    dialogGeometry: ResizeGeometry | null;
    userFirstName: string;
};

const initialState: ChatbotState = {
    entries: [],
    isLoading: false,
    hasError: false,
    isOpen: false,
    isInitialized: false,
    fullScreen: false,
    dialogGeometry: null,
    userFirstName: '',
};

const randomId = (): string => globalThis.crypto?.randomUUID?.() ?? `m_${Date.now()}_${Math.random().toString(36).slice(2)}`;

export const ChatbotStore = signalStore(
    { providedIn: 'root' },
    withDevtools('chatbot'),
    withState(initialState),
    withMethods((state, chatbotService = inject(ChatbotService)) => ({
        giveFeedback: rxMethod<{ messageId: string; wasHelpful: boolean }>(
            pipe(
                filter(({ messageId, wasHelpful }) => {
                    const entry = state.entries().find((e) => e.chatbotConversationMessageId === messageId);
                    const feedback: ChatbotFeedback = wasHelpful ? 'helpful' : 'not-helpful';
                    return entry?.feedback !== feedback;
                }),
                switchMap(({ messageId, wasHelpful }) => {
                    const feedback: ChatbotFeedback = wasHelpful ? 'helpful' : 'not-helpful';
                    const previous = state.entries().find((e) => e.chatbotConversationMessageId === messageId)?.feedback;
                    patchState(state, {
                        entries: state
                            .entries()
                            .map((e) => (e.chatbotConversationMessageId === messageId ? { ...e, feedback: feedback } : e)),
                    });
                    return chatbotService.giveFeedback(messageId, wasHelpful).pipe(
                        catchError(() => {
                            patchState(state, {
                                entries: state
                                    .entries()
                                    .map((e) => (e.chatbotConversationMessageId === messageId ? { ...e, feedback: previous } : e)),
                            });
                            return EMPTY;
                        }),
                    );
                }),
            ),
        ),
        sendMessage: rxMethod<string>(
            pipe(
                tap((text) => {
                    const userEntry: ChatbotEntry = {
                        id: randomId(),
                        author: 'user',
                        text,
                        chatbotConversationMessageId: null,
                        timestamp: new Date().toISOString(),
                    };
                    patchState(state, {
                        entries: [...state.entries(), userEntry],
                        isLoading: true,
                        hasError: false,
                    });
                }),
                switchMap((text) => {
                    const payload: ChatMessage = { message: text };
                    return chatbotService.sendMessage(payload).pipe(
                        tap((response) => {
                            const botEntry: ChatbotEntry = {
                                id: randomId(),
                                author: 'bot',
                                text: response.answer,
                                followupQuestions: response.followupQuestions,
                                sources: response.sources,
                                chatbotConversationMessageId: response.chatbotConversationMessageId,
                                timestamp: new Date().toISOString(),
                            };
                            patchState(state, {
                                entries: [...state.entries(), botEntry],
                                isLoading: false,
                            });
                        }),
                        catchError(() => {
                            patchState(state, { hasError: true, isLoading: false });
                            return EMPTY;
                        }),
                    );
                }),
            ),
        ),
    })),
    withMethods(
        (store, chatbotService = inject(ChatbotService), languageService = inject(LanguageService), userService = inject(UserService)) => {
            const buildGreetingEntry = (): ChatbotEntry => ({
                id: randomId(),
                author: 'bot',
                text: languageService.translate(RESOURCES.petsxl.ui.chatbot.initial_greeting, [
                    { name: 'User_FirstName', text: store.userFirstName() },
                ]),
                chatbotConversationMessageId: null,
                timestamp: new Date().toISOString(),
            });

            const ensureUserLoaded$ = (): Observable<unknown> =>
                store.userFirstName()
                    ? of(undefined)
                    : userService.getUser().pipe(tap((user) => patchState(store, { userFirstName: user.firstName ?? '' })));

            return {
                markClosed(): void {
                    patchState(store, { isOpen: false });
                },
                setFullScreen(value: boolean): void {
                    patchState(store, { fullScreen: value });
                },
                setDialogGeometry(geometry: ResizeGeometry | null): void {
                    patchState(store, { dialogGeometry: geometry });
                },
                closeAndReset(): void {
                    patchState(store, {
                        entries: [],
                        isOpen: false,
                        isLoading: false,
                        hasError: false,
                        isInitialized: false,
                        fullScreen: false,
                        dialogGeometry: null,
                    });
                },
                resetForLogout(): void {
                    patchState(store, initialState);
                },
                openDialog: rxMethod<string | undefined>(
                    pipe(
                        tap(() => {
                            patchState(store, { isOpen: true });
                        }),
                        filter(() => !store.isInitialized()),
                        tap(() => {
                            patchState(store, { isLoading: false, hasError: false });
                        }),
                        switchMap((initialMessage) =>
                            ensureUserLoaded$().pipe(
                                switchMap(() => chatbotService.initialize()),
                                tap(() => {
                                    patchState(store, {
                                        isInitialized: true,
                                        entries: [...store.entries(), buildGreetingEntry()],
                                    });
                                    const trimmed = initialMessage?.trim();
                                    if (trimmed) {
                                        store.sendMessage(trimmed);
                                    }
                                }),
                                catchError(() => {
                                    patchState(store, { hasError: true });
                                    return EMPTY;
                                }),
                            ),
                        ),
                    ),
                ),
                startNewChat: rxMethod<void>(
                    pipe(
                        tap(() => {
                            patchState(store, {
                                entries: [],
                                isOpen: true,
                                isLoading: false,
                                hasError: false,
                                isInitialized: false,
                            });
                        }),
                        switchMap(() =>
                            ensureUserLoaded$().pipe(
                                switchMap(() => chatbotService.initialize()),
                                tap(() => {
                                    patchState(store, {
                                        isInitialized: true,
                                        entries: [...store.entries(), buildGreetingEntry()],
                                    });
                                }),
                                catchError(() => {
                                    patchState(store, { hasError: true });
                                    return EMPTY;
                                }),
                            ),
                        ),
                    ),
                ),
            };
        },
    ),
    withMethods((store) => ({
        openWith(options: { initialMessage?: string; fullScreen?: boolean } = {}): void {
            if (options.fullScreen !== undefined) {
                patchState(store, { fullScreen: options.fullScreen });
            }
            store.openDialog(options.initialMessage);
        },
    })),
    withHooks({
        onInit(store): void {
            const loginService = inject(LoginService);
            loginService
                .onUserLoginChanged()
                .pipe(
                    takeUntilDestroyed(),
                    filter((state: LoginState) => !state.isLoggedIn),
                )
                .subscribe(() => store.resetForLogout());
        },
    }),
);
